import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Clock, Thermometer, CloudRain, Sun, CircleNotch, Info, MapPin,
} from "@phosphor-icons/react";
import LocationPicker from "@/components/LocationPicker";
import DualExplainer from "@/components/DualExplainer";
import GlossaryTerm from "@/components/GlossaryTerm";
import { fetchHeritageLocation, fetchHeritageRegion, fetchRegions } from "@/lib/api";

// Quick-pick set of regions known to return data reliably (DE/AT Bundesländer
// + select OOe districts). Slugs match the IDs returned by `/api/regions`. We
// resolve labels at runtime so renames in the backend don't desync the UI.
const QUICK_REGIONS = [
  "de-bayern", "de-baden-wuerttemberg", "de-berlin", "de-hamburg", "de-sachsen",
  "at-salzburg", "at-wien", "at-steiermark", "at-tirol", "at-ooe-linz-stadt",
];

function DeltaRow({ icon: Icon, label, value, suffix, delta, suffixDelta = "", positiveColor = "#00FA9A", negativeColor = "#FF5500" }) {
  const color =
    delta == null ? "#A1A1AA" :
    delta > 0 ? (label.includes("Niederschlag") ? positiveColor : negativeColor) :
    (label.includes("Niederschlag") ? negativeColor : positiveColor);
  return (
    <div className="grid grid-cols-[24px_1fr_auto_auto] items-center gap-2 py-2 border-b border-white/10 last:border-b-0">
      <Icon size={14} className="text-[#00FA9A]" />
      <span className="text-sm">{label}</span>
      <span className="font-mono text-sm">{value == null ? "–" : value}{suffix}</span>
      {delta != null && (
        <span className="font-mono text-xs font-medium" style={{ color }}>
          {delta > 0 ? "+" : ""}{delta}{suffixDelta}
        </span>
      )}
    </div>
  );
}

function ComparisonCard({ c }) {
  return (
    <div className="eid-card p-4" data-testid={`heritage-cmp-${c.years_ago}`}>
      <div className="flex items-center justify-between">
        <div className="eid-label">Vor {c.years_ago} Jahren</div>
        <span className="text-[10px] font-mono text-zinc-500">
          {(c.period_start || "").slice(0, 10)} – {(c.period_end || "").slice(0, 10)}
        </span>
      </div>
      <p className="text-sm mt-2 italic text-[#00FA9A]">„{c.story}"</p>
      <div className="mt-3">
        <DeltaRow icon={Thermometer} label="Temperatur" value={c.temperature_avg_c} suffix=" °C" delta={c.delta_temperature_c != null ? -c.delta_temperature_c : null} suffixDelta=" °C" />
        <DeltaRow icon={CloudRain} label="Niederschlag" value={c.precipitation_sum_mm} suffix=" mm" delta={c.delta_precipitation_pct != null ? -c.delta_precipitation_pct : null} suffixDelta=" %" />
        <DeltaRow icon={Sun} label="Sonne" value={c.sunshine_hours} suffix=" h" delta={c.delta_sunshine_pct != null ? -c.delta_sunshine_pct : null} suffixDelta=" %" />
      </div>
    </div>
  );
}

export default function Heritage() {
  const [picked, setPicked] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [activeQuick, setActiveQuick] = useState(null);

  useEffect(() => {
    fetchRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  const onPick = async (p) => {
    setPicked(p);
    setActiveQuick(null);
    if (!p?.loc_id) { setData(null); return; }
    setLoading(true);
    try { setData(await fetchHeritageLocation(p.loc_id, 60)); }
    catch { setData(null); }
    finally { setLoading(false); }
  };

  const pickRegion = async (regionId) => {
    setActiveQuick(regionId);
    setLoading(true);
    setData(null);
    try {
      const result = await fetchHeritageRegion(regionId, 60);
      setData(result);
      const r = regions.find((x) => x.id === regionId);
      if (r) setPicked({ label: r.name || r.label, lat: r.latitude, lon: r.longitude });
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const quickList = QUICK_REGIONS
    .map((id) => regions.find((r) => r.id === id))
    .filter(Boolean);

  return (
    <div className="space-y-6" data-testid="heritage-page">
      <Helmet><title>Was würde mein Opa sagen? · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8" data-testid="heritage-hero"
        style={{ background: "linear-gradient(135deg, #141414 0%, #181818 100%)" }}>
        <div className="flex items-center gap-2"><Clock size={20} className="text-[#00FA9A]" weight="fill" /><div className="eid-label">60 Jahre Klima</div></div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
          Was würde mein Opa sagen?
        </h1>
        <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Wir vergleichen dein Wetter heute mit <strong>demselben Zeitraum vor 20, 40 und 60 Jahren</strong> —
          auf deinen Koordinaten. Klimawandel nicht aus dem Fernseher, sondern aus dem
          eigenen Acker.
        </p>
      </div>

      <div className="eid-card p-5">
        <LocationPicker onPick={onPick} testid="heritage-picker" />
      </div>

      {quickList.length > 0 && (
        <div
          className="eid-card p-5 bg-[#161616]/40"
          data-testid="heritage-quick-picks"
        >
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={14} className="text-[#00FA9A]" weight="fill" />
            <div className="eid-label">Direkt-Auswahl · garantiert verfügbar</div>
          </div>
          <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
            Falls deine PLZ keine Daten lädt: Diese 10 Regionen liefern verlässlich
            Vergleichswerte aus dem ERA5-Archiv.
          </p>
          <div className="flex flex-wrap gap-2">
            {quickList.map((r) => {
              const active = activeQuick === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => pickRegion(r.id)}
                  className={`px-3 py-2 text-sm border transition rounded-sm font-mono ${
                    active
                      ? "bg-[#00FA9A] text-black border-[#00FA9A]/40"
                      : "bg-[#121212] text-white border-white/15 hover:border-[#00FA9A]/40"
                  }`}
                  data-testid={`heritage-quick-${r.id}`}
                >
                  {r.name || r.label || r.id}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <DualExplainer
        title="Was vergleichen wir hier — und wie?"
        testid="heritage-explainer"
        simple={
          <>
            <p>
              Wir nehmen <strong>die letzten 60 Tage</strong> bei dir vor Ort und schauen, wie
              das gleiche Zeitfenster <strong>vor 20, 40 und 60 Jahren</strong> ausgesehen hat —
              auf <em>denselben Koordinaten</em>. Drei Werte zählen: Durchschnittstemperatur,
              Niederschlagssumme und Sonnenstunden.
            </p>
            <p>
              Die kleinen Pfeile rechts zeigen die <strong>Veränderung gegenüber damals</strong>:
              grün heißt es war damals besser für die Pflanzen, rot heißt es war damals schlechter.
              So spürst du Klimawandel nicht im Fernsehen, sondern auf deinem eigenen Acker.
            </p>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Vergleich vor allem die Niederschlagswerte — sie
              zeigen am direktesten, ob deine Saatzeit-Tradition noch trägt oder ob du Sorten
              und Termine anpassen solltest.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Datenquelle:</strong> Open-Meteo Historical Weather API, dahinter{" "}
              <GlossaryTerm word="ERA5">ERA5-Reanalyse</GlossaryTerm> des ECMWF (Auflösung
              ~31 km, stündlich, seit 1940). Es handelt sich nicht um Messdaten einer
              einzelnen Station, sondern um modellierte Werte, die mit allen verfügbaren
              Beobachtungen abgeglichen wurden.
            </p>
            <p>
              <strong>Methodik:</strong> Für jeden Vergleichs-Slot (heute, vor 20a, 40a, 60a)
              wird das gleiche Datumsfenster (Default: 60 Tage) aus dem ERA5-Archiv extrahiert
              und gemittelt. Δ°C ist die absolute Differenz, Δ% bei Niederschlag/Sonne die
              relative Veränderung gegenüber dem heutigen Wert.
            </p>
            <p>
              <strong>Grenzen:</strong> ERA5 ist eine Reanalyse, keine Messung. Punktuell kann
              ein Tal oder Höhenrücken stark abweichen. Aussage über Welt-/Landesklima ist nicht
              möglich — dazu siehe{" "}
              <a className="underline" href="https://www.ipcc.ch/report/ar6/syr/" target="_blank" rel="noreferrer">
                IPCC AR6
              </a>.
            </p>
          </>
        }
      />

      {loading && (
        <div className="eid-card p-6 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Lade 60 Jahre ERA5 …</div>
      )}

      {data?.current && !loading && (
        <>
          <div className="eid-card p-5" data-testid="heritage-current">
            <div className="eid-label">Heute · letzte {data.window_days} Tage</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div className="p-3 bg-[#0A0A0A] rounded-sm">
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <Thermometer size={12} /> Temperatur ∅
                </div>
                <div className="text-2xl font-medium font-mono mt-1">
                  {data.current.temperature_avg_c ?? "–"} °C
                </div>
              </div>
              <div className="p-3 bg-[#0A0A0A] rounded-sm">
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <CloudRain size={12} /> Niederschlag Σ
                </div>
                <div className="text-2xl font-medium font-mono mt-1">
                  {data.current.precipitation_sum_mm ?? "–"} mm
                </div>
              </div>
              <div className="p-3 bg-[#0A0A0A] rounded-sm">
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <Sun size={12} /> Sonne Σ
                </div>
                <div className="text-2xl font-medium font-mono mt-1">
                  {data.current.sunshine_hours ?? "–"} h
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(data.comparisons || []).map((c) => <ComparisonCard key={c.years_ago} c={c} />)}
          </div>

          <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
            <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
            <span>
              Datenquelle: {data.source}. Zeigt die <em>wahrgenommenen</em> Bedingungen auf deinen
              Koordinaten — keine Aussage über Land- oder Weltklima. Für den großen Blick siehe {" "}
              <a className="underline" href="https://www.ipcc.ch/report/ar6/syr/" target="_blank" rel="noreferrer">IPCC AR6</a>.
            </span>
          </div>
        </>
      )}

      {!picked && !loading && (
        <div className="eid-card p-6 text-center text-sm text-zinc-400" data-testid="heritage-empty">
          <Clock size={24} className="mx-auto text-[#00FA9A]" />
          <div className="mt-2">Wähl eine Region — und hör, was dein Acker erzählt.</div>
        </div>
      )}
    </div>
  );
}
